import { ChangeEvent, FC, useContext, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { DataContext } from "@/provider/DataContextProvider";
import {
  useGetCustomer,
  useSetDeviceByUuid,
  useSetEntryCustomData,
} from "@/services/api";
import { SIM_ALIAS_MAX_LENGTH } from "@/utils/constants";
import { isBusinessCustomer } from "@/utils/customerUtils";
import {
  getEntry,
  isValidSimAlias,
  withPreservedSimAlias,
  withSimAlias,
} from "@/utils/dataContextHelpers";
import { getDefaultAliasKey, getDeviceForProduct } from "@/utils/productMatrix";

type AliasForm = { alias: string };

interface Props {
  showErrors?: boolean;
}

export const DeviceDetailsForm: FC<Props> = ({ showErrors }) => {
  const { dataContext, setDataContext } = useContext(DataContext);
  const { t } = useTranslation("translation", {
    keyPrefix: "Onboarding.sections.checkout.summary",
  });
  const { t: tDefault } = useTranslation();

  const { data: customerData } = useGetCustomer();
  // Only a customer with a known, non-business customerType is treated as
  // private and gets the product-matrix default. A still-loading/failed query
  // (customerData undefined) OR a customer whose customerType/id is absent
  // stays non-private, so the default is never prefilled on an unknown type —
  // a business customer with slow-loading data would otherwise get it written.
  // Business customers name every SIM themselves (cost centre / own label).
  const isPrivate =
    customerData?.customerType?.id != null && !isBusinessCustomer(customerData);

  const entry = getEntry(dataContext);
  const productId = entry?.options?.[0]?.product?.id ?? entry?.product?.id;
  const customData = entry?.customData;
  const basketAlias = ((customData?.["SimAlias"] as string) || "").trim();
  const userHasEditedRef = useRef(false);
  const lastPersistedAliasRef = useRef(basketAlias);

  const resolveDefaultAlias = () => {
    const aliasKey = getDefaultAliasKey(productId);

    return aliasKey ? t(aliasKey as any) : "";
  };

  const {
    register,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<AliasForm>({
    defaultValues: {
      alias: basketAlias || (isPrivate ? resolveDefaultAlias() : ""),
    },
  });

  const { mutate: setDevice } = useSetDeviceByUuid({
    mutation: {
      onSuccess: (data) => {
        setDataContext((prev) => ({
          ...prev,
          basket: withPreservedSimAlias(prev.basket, data),
        }));
      },
    },
  });

  const { mutate: setEntryCustomData } = useSetEntryCustomData({
    mutation: {
      onSuccess: (data) => {
        setDataContext((prev) => ({
          ...prev,
          basket: withPreservedSimAlias(prev.basket, data),
        }));
      },
    },
  });

  const persistSimAlias = (alias: string) => {
    if (!dataContext.basket?.id || !entry?.uuid) {
      return;
    }

    // Optimistic local update so checkout submit validation sees the alias
    // immediately, without waiting for the API mutation to round-trip.
    setDataContext((prev) => {
      const nextBasket = withSimAlias(prev.basket, alias);

      if (!nextBasket) {
        return prev;
      }

      return {
        ...prev,
        basket: nextBasket,
      };
    });

    setEntryCustomData({
      basketId: dataContext.basket.id,
      uuid: entry.uuid,
      data: {
        SimAlias: alias,
      },
    });
    lastPersistedAliasRef.current = alias;
  };

  useEffect(() => {
    if (dataContext.basket?.id && entry?.uuid && productId) {
      const { id: deviceId } = getDeviceForProduct(productId);
      const deviceMismatch = entry.device?.id !== deviceId;

      // Always correct a missing or wrong device (e.g. leftover Flat Home router
      // on a Flat 10 entry) — wrong device can make precharged pricing non-zero.
      if (!entry.device || deviceMismatch) {
        setDevice({
          basketId: dataContext.basket.id,
          uuid: entry.uuid,
          deviceId,
        });
      }

      // Never overwrite a name the user is editing / has already changed.
      if (userHasEditedRef.current) {
        return;
      }

      // Only a resolved private customer gets a default. Unknown (still loading)
      // and business both stay empty — business customers name every SIM
      // themselves. Re-runs once the customer type resolves (isPrivate dep).
      if (!isPrivate) {
        return;
      }

      if (!basketAlias || deviceMismatch) {
        const defaultAliasTranslated = resolveDefaultAlias();

        if (defaultAliasTranslated) {
          setValue("alias", defaultAliasTranslated);
          persistSimAlias(defaultAliasTranslated);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    entry?.uuid,
    dataContext.basket?.id,
    productId,
    entry?.device?.id,
    isPrivate,
  ]);

  // Keep local input in sync if the basket already has an alias (e.g. Flat Home
  // prefill from SimVerification) that arrives after first mount.
  useEffect(() => {
    if (userHasEditedRef.current) {
      return;
    }

    if (basketAlias) {
      setValue("alias", basketAlias);
    }
  }, [basketAlias, setValue]);

  useEffect(() => {
    if (showErrors) {
      trigger("alias");
    }
  }, [showErrors, trigger]);

  const handleAliasChange = (e: ChangeEvent<HTMLInputElement>) => {
    userHasEditedRef.current = true;
    const trimmed = e.target.value.trim();

    if (!isValidSimAlias(trimmed)) {
      return;
    }

    setDataContext((prev) => {
      const nextBasket = withSimAlias(prev.basket, trimmed);

      if (!nextBasket) {
        return prev;
      }

      return {
        ...prev,
        basket: nextBasket,
      };
    });
  };

  const handleAliasBlur = () => {
    const currentAlias = getValues("alias") ?? "";
    const trimmedDeviceName = currentAlias.trim();
    // Only a resolved private customer gets a default fallback. Business (and
    // not-yet-resolved) keep an empty/invalid field empty so the required-field
    // error surfaces instead of snapping to a name.
    const defaultAlias = isPrivate ? resolveDefaultAlias() : "";
    const nextAlias = isValidSimAlias(trimmedDeviceName)
      ? trimmedDeviceName
      : isValidSimAlias(basketAlias)
        ? basketAlias
        : defaultAlias;

    if (nextAlias !== currentAlias) {
      setValue("alias", nextAlias, { shouldValidate: true });
    }

    if (!isValidSimAlias(trimmedDeviceName) && nextAlias === defaultAlias) {
      userHasEditedRef.current = false;
    }

    if (
      dataContext.basket?.id &&
      entry?.uuid &&
      isValidSimAlias(nextAlias) &&
      nextAlias !== lastPersistedAliasRef.current
    ) {
      persistSimAlias(nextAlias);
    }
  };

  return (
    <div>
      <FormField<AliasForm>
        id="checkout-sim-alias"
        name="alias"
        label={tDefault("Common.label.subscriptionName")}
        errors={errors}
        register={register}
        options={{
          maxLength: SIM_ALIAS_MAX_LENGTH,
          validate: (v: string) =>
            isValidSimAlias(v?.trim()) || t("errorDeviceName"),
          onChange: handleAliasChange,
        }}
        onBlur={handleAliasBlur}
        suppressError={!showErrors}
      />
    </div>
  );
};
